/**
 * Football on the Sports tag page (and its sub-tags):
 *  - sportsTableGroup(slug): the NPFL's top four, as a section of the tag's About card (like the NGX's top
 *    companies on Finance & Business), opening to the full table
 *  - SportsScores: latest results (live matches first) and upcoming fixtures for the NPFL, EPL and UCL, above
 *    the feed
 * The data comes from GET /api/voucha-markets/sports: the EPL and UCL from football-data.org (saved by
 * scripts/sports/refresh_sports.php; the browser never calls it), the NPFL typed in by an admin (Admin > Voucha
 * Markets). Nothing shows until there is data. Dates and kickoff times show in the reader's own time zone.
 */
import app from 'flarum/forum/app';
const LIVE = ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'LIVE', 'INT', 'SUSP'];
// Times show in the reader's own time zone, the one their device is set to (more reliable than guessing from
// the IP address, which a VPN or a mobile network can put in another city). Nigeria's time if it can't be read.
const TZ = (() => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone) {
      new Intl.DateTimeFormat('en-GB', {
        timeZone: zone
      }).format(new Date()); // throws for a zone it doesn't know
      return zone;
    }
  } catch (e) {}
  return 'Africa/Lagos';
})();

/** The note under the title: "Your time (EDT)", or the city when the zone has no short name ("Your time (Lagos)") */
function zoneNote() {
  if (TZ === 'Africa/Lagos') return 'Times in WAT';
  let short = '';
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ,
      timeZoneName: 'short'
    }).formatToParts(new Date()).find(p => p.type === 'timeZoneName');
    short = part ? part.value : '';
  } catch (e) {}
  const city = TZ.split('/').pop().replace(/_/g, ' ');
  return 'Your time (' + (short && !/^(GMT|UTC)/.test(short) ? short : city) + ')';
}
let data = null; // { updated, competitions: [...] }
let loading = false;
let refreshTimer = null;
export function isSportsTag(slug) {
  if (!slug) return false;
  if (slug === 'sports') return true;
  const tag = (app.store.all('tags') || []).find(t => t.slug() === slug);
  const parent = tag && tag.parent && tag.parent();
  return !!(parent && parent.slug && parent.slug() === 'sports');
}
function load(force) {
  if (loading || data && !force) return;
  loading = true;
  app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/voucha-markets/sports',
    errorHandler: () => {}
  }).then(r => {
    data = r && Array.isArray(r.competitions) ? r : {
      competitions: []
    };
    loading = false;
    m.redraw();
  }).catch(() => {
    data = data || {
      competitions: []
    };
    loading = false;
  });
}
const competitions = () => data && data.competitions || [];
const isLive = f => LIVE.indexOf(f.status) !== -1;
function localDay(ts) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date(ts * 1000));
}
function dayLabel(ts) {
  const day = localDay(ts);
  const now = Date.now() / 1000;
  if (day === localDay(now)) return 'Today';
  if (day === localDay(now + 86400)) return 'Tomorrow';
  if (day === localDay(now - 86400)) return 'Yesterday';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  }).format(new Date(ts * 1000));
}
function clock(ts) {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(new Date(ts * 1000));
}

/** What goes in a match's side column: the time, FT, HT or the minute */
function statusText(f) {
  const s = f.status;
  if (isLive(f)) return s === 'HT' ? 'HT' : s === 'P' ? 'Pens' : s === 'BT' ? 'Break' : s === 'SUSP' ? 'Suspended' : s === 'INT' ? 'Stopped' : f.elapsed ? f.elapsed + '’' : 'Live';
  if (s === 'FT') return 'FT';
  if (s === 'AET') return 'AET';
  if (s === 'PEN') return 'Pens';
  if (s === 'PST') return 'Postponed';
  if (s === 'CANC') return 'Cancelled';
  if (s === 'ABD') return 'Abandoned';
  if (s === 'AWD' || s === 'WO') return 'Awarded';
  if (s === 'TBD') return 'TBC';
  return f.time ? clock(f.time) : '';
}
const logo = src => src ? m('img.VouchaScores-logo', {
  src,
  alt: '',
  width: 16,
  height: 16,
  loading: 'lazy',
  onerror: e => e.target.style.visibility = 'hidden'
}) : null; // the hand-entered NPFL has no badges

// ---------- About card: NPFL top four ----------

let fullTable = false;
export function sportsTableGroup(slug) {
  if (!isSportsTag(slug)) return null;
  load();
  const npfl = competitions().find(c => c.key === 'npfl');
  const table = npfl && npfl.tables && npfl.tables[0];
  if (!table || !table.rows || !table.rows.length) return null;
  const rows = fullTable ? table.rows : table.rows.slice(0, 4);
  return [m('h4', 'NPFL table'), m('div.VouchaLeague-head', [m('span', '#'), m('span', 'Club'), m('span', 'P'), m('span', 'GD'), m('span', 'Pts')]), rows.map(r => m('div.VouchaLeague-row', {
    title: r.won === null || r.won === undefined ? r.team : r.team + ': ' + r.won + ' won, ' + r.drawn + ' drawn, ' + r.lost + ' lost'
  }, [m('span.VouchaLeague-rank', r.rank), m('span.VouchaLeague-team', [logo(r.logo), m('span', r.team)]), m('span', r.played), m('span', (r.gd > 0 ? '+' : '') + r.gd), m('span.VouchaLeague-pts', r.points)])), table.rows.length > 4 ? m('button.Voucha-topStocks-more.VouchaLeague-more', {
    type: 'button',
    onclick: () => fullTable = !fullTable
  }, fullTable ? 'Show top 4' : 'Full table →') : null];
}

// ---------- Above the feed: results, fixtures and tables ----------

/**
 * The tab a competition opens on: Results while a match is on or just after a round, Fixtures when the next
 * match is nearer than the last result (a week-old round shouldn't hide today's games).
 */
function autoTab(c) {
  if (c.live) return 'results';
  const now = Date.now() / 1000;
  const last = c.results && c.results[0] && c.results[0].time;
  const next = c.fixtures && c.fixtures[0] && c.fixtures[0].time;
  if (!last) return 'fixtures';
  if (!next) return 'results';
  return next - now < now - last ? 'fixtures' : 'results';
}

/** League table (top six, then all) or group tables (all of each) */
function tablesView(tables, more, toggle) {
  const groups = tables.length > 1;
  const one = t => {
    const rows = groups || more ? t.rows : t.rows.slice(0, 6);
    return m('div.VouchaScores-table', [groups && t.name ? m('h4.VouchaScores-group', t.name) : null, m('div.VouchaLeague-head', [m('span', '#'), m('span', 'Club'), m('span', 'P'), m('span', 'GD'), m('span', 'Pts')]), rows.map(r => m('div.VouchaLeague-row', [m('span.VouchaLeague-rank', r.rank), m('span.VouchaLeague-team', [logo(r.logo), m('span', r.team)]), m('span', r.played), m('span', (r.gd > 0 ? '+' : '') + r.gd), m('span.VouchaLeague-pts', r.points)]))]);
  };
  return m('div.VouchaScores-tables' + (groups ? '.is-groups' : ''), [tables.map(one), !groups && tables[0].rows.length > 6 ? m('button.VouchaScores-more', {
    type: 'button',
    onclick: toggle
  }, more ? 'Show top 6' : 'Full table') : null]);
}
function matchRow(f) {
  const played = f.goals && f.goals[0] !== null && f.goals[0] !== undefined;
  const live = isLive(f);
  const side = (team, goals, pens) => m('div.VouchaScores-team' + (team.winner ? '.is-winner' : ''), [logo(team.logo), m('span.VouchaScores-name', team.name), played ? m('span.VouchaScores-goals', [goals, pens !== null && pens !== undefined ? m('small', ' (' + pens + ')') : null]) : null]);
  const pens = f.penalties || [null, null];
  return m('li.VouchaScores-match' + (live ? '.is-live' : ''), {
    key: f.id
  }, [m('div.VouchaScores-when', [m('span.VouchaScores-day', live && f.elapsed ? 'Live' : f.time ? dayLabel(f.time) : ''), m('span.VouchaScores-status', statusText(f))]), m('div.VouchaScores-teams', [side(f.home, f.goals && f.goals[0], pens[0]), side(f.away, f.goals && f.goals[1], pens[1])])]);
}
export class SportsScores {
  oninit() {
    this.comp = null;
    this.tab = null; // null: whichever is nearer now (autoTab)
    this.more = false;
    load();
  }
  onremove() {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
  view() {
    const list = competitions().filter(c => c.results && c.results.length || c.fixtures && c.fixtures.length);
    if (!list.length) return null;

    // while a match is on, the scores refresh every two minutes (from our cache, which follows live games)
    const anyLive = list.some(c => c.live);
    if (anyLive && !refreshTimer) refreshTimer = setInterval(() => load(true), 120000);
    if (!anyLive && refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
    let comp = list.find(c => c.key === this.comp);
    if (!comp) comp = list.find(c => c.live) || list.find(c => c.key === 'npfl') || list[0];
    const results = comp.results || [];
    const fixtures = comp.fixtures || [];
    const tables = (comp.tables || []).filter(t => t && t.rows && t.rows.length);
    let tab = this.tab || autoTab(comp);
    if (tab === 'table' && !tables.length) tab = 'results';
    if (tab === 'results' && !results.length) tab = fixtures.length ? 'fixtures' : tables.length ? 'table' : 'results';
    if (tab === 'fixtures' && !fixtures.length) tab = results.length ? 'results' : 'table';
    const matches = tab === 'results' ? results : tab === 'fixtures' ? fixtures : [];
    const shown = this.more ? matches : matches.slice(0, 5);
    const pick = key => () => {
      this.comp = key;
      this.tab = null;
      this.more = false;
    };
    const setTab = t => () => {
      this.tab = t;
      this.more = false;
    };
    return m('section.VouchaScores', [m('div.VouchaScores-header', [m('h3.VouchaScores-title', 'Scores and fixtures'), m('span.VouchaScores-note', zoneNote())]), m('div.VouchaScores-comps', {
      role: 'tablist',
      'aria-label': 'Competition'
    }, list.map(c => m('button.VouchaScores-comp' + (c.key === comp.key ? '.is-active' : ''), {
      type: 'button',
      role: 'tab',
      'aria-selected': c.key === comp.key ? 'true' : 'false',
      title: c.name,
      onclick: pick(c.key)
    }, [c.live ? m('span.VouchaScores-liveDot', {
      'aria-label': 'Live'
    }) : null, c.short]))), m('div.VouchaScores-tabs', [m('button.VouchaScores-tab' + (tab === 'results' ? '.is-active' : ''), {
      type: 'button',
      disabled: !results.length,
      onclick: setTab('results')
    }, comp.live ? 'Live and results' : 'Results'), m('button.VouchaScores-tab' + (tab === 'fixtures' ? '.is-active' : ''), {
      type: 'button',
      disabled: !fixtures.length,
      onclick: setTab('fixtures')
    }, 'Fixtures'), tables.length ? m('button.VouchaScores-tab' + (tab === 'table' ? '.is-active' : ''), {
      type: 'button',
      onclick: setTab('table')
    }, tables.length > 1 ? 'Groups' : 'Table') : null]), tab === 'table' ? tablesView(tables, this.more, () => this.more = !this.more) : m('ul.VouchaScores-list', shown.map(matchRow)), tab !== 'table' && matches.length > 5 ? m('button.VouchaScores-more', {
      type: 'button',
      onclick: () => this.more = !this.more
    }, this.more ? 'Show fewer' : 'Show ' + (matches.length - 5) + ' more') : null, comp.source && comp.source.url ? m('p.VouchaScores-source', ['Data: ', m('a', {
      href: comp.source.url,
      target: '_blank',
      rel: 'noopener'
    }, comp.source.name || 'source'), ' (CC BY-SA)']) : comp.key !== 'npfl' ? m('p.VouchaScores-source', 'Data: football-data.org') : null]);
  }
}